import { useCallback, useState } from 'react'

import { useTailgateGridTestIncidentVideo } from '../hooks/useTailgateGridTestIncidentVideo'
import { TailgateGridTestJobResult } from '../hooks/useTailgateGridTestJob'
import { useTailgateGridTestSidecar } from '../hooks/useTailgateGridTestSidecar'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Box, Checkbox, Flex, Text } from '@chakra-ui/react'

import {
  Box as DarkBox,
  Checkbox as DarkCheckbox,
  Flex as DarkFlex,
  Text as DarkText,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { PlaybackDiagnosticsIProps, VideoPlayer } from '@/features/video-player'
import { TailgateScalingMode } from '@/graphql/generated/schemas'

import { TailgateGridTestOverlay } from './TailgateGridTestOverlay'

interface TailgateGridTestPlayerIProps {
  colorScheme?: ColorScheme
  incidentId?: string
  isEnabled: boolean
  job: TailgateGridTestJobResult
}

export const TailgateGridTestPlayer = ({
  colorScheme,
  incidentId,
  isEnabled,
  job,
}: TailgateGridTestPlayerIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null
  )
  const [playbackError, setPlaybackError] = useState<string | null>(null)
  const [isCodecUnsupported, setCodecUnsupported] = useState(false)
  const [isFrameCallbackUnsupported, setFrameCallbackUnsupported] =
    useState(false)
  const [layers, setLayers] = useState({
    zones: true,
    boxes: true,
    trackIds: true,
    counts: true,
  })

  const {
    isError: isVideoError,
    isLoading: isVideoLoading,
    videoUrl,
  } = useTailgateGridTestIncidentVideo({ incidentId, isEnabled })
  const {
    isError: isSidecarError,
    isLoading: isSidecarLoading,
    sidecar,
  } = useTailgateGridTestSidecar({
    isEnabled,
    jobId: job.id,
    sidecarAssetUrl: job.sidecarAssetUrl,
  })

  const handlePlaybackError = useCallback(
    (diagnostics: PlaybackDiagnosticsIProps) => {
      if (
        diagnostics.media_error_code === 3 ||
        diagnostics.media_error_code === 4
      ) {
        setCodecUnsupported(true)
        setPlaybackError(t('tailgate:grid_test_v2.codec_unsupported'))
        return
      }

      setCodecUnsupported(false)
      setPlaybackError(
        diagnostics.media_error_code === 2
          ? t('tailgate:grid_test_v2.video_network_error')
          : t('tailgate:grid_test_v2.video_error')
      )
    },
    [t]
  )

  const toggleLayer = (layer: keyof typeof layers) =>
    setLayers((current) => ({ ...current, [layer]: !current[layer] }))
  const handleFrameCallbackUnsupported = useCallback(
    () => setFrameCallbackUnsupported(true),
    []
  )
  const handlePlaybackStalled = useCallback(() => {
    setCodecUnsupported(false)
    setPlaybackError(t('tailgate:grid_test_v2.video_stalled'))
  }, [t])

  const isZonesDisabled =
    job.scalingMode === TailgateScalingMode.None ||
    sidecar?.scaling_mode === 'none'
  const playerError = isVideoError
    ? t('tailgate:grid_test_v2.video_error')
    : playbackError

  const overlay = sidecar ? (
    <TailgateGridTestOverlay
      layers={layers}
      onUnsupported={handleFrameCallbackUnsupported}
      sidecar={sidecar}
      videoElement={videoElement}
    />
  ) : undefined

  const videoPlayer = (
    <VideoPlayer
      errorLabel={playerError ?? undefined}
      isProcessing={isVideoLoading}
      onPlaybackError={handlePlaybackError}
      onPlaybackStalled={handlePlaybackStalled}
      onVideoElementReady={setVideoElement}
      overlay={overlay}
      showControls
      showToggleLiveButton={false}
      suppressGlobalPlayState
      url={videoUrl ?? ''}
    />
  )

  if (colorScheme) {
    const tokenPathPrefix = tokenPrefix(colorScheme)
    const borderColor = tokenVar(`colors.${tokenPathPrefix}border.default`)
    const secondaryText = tokenVar(`colors.${tokenPathPrefix}text.secondary`)

    return (
      <DarkBox>
        <DarkFlex
          border='1px solid'
          borderColor={borderColor}
          gap='16px'
          mb='12px'
          p='12px'
          wrap='wrap'
        >
          <DarkCheckbox
            checked={layers.zones && !isZonesDisabled}
            colorScheme={colorScheme}
            disabled={isZonesDisabled}
            label={t('tailgate:grid_test_v2.layer_zones')}
            onChange={() => toggleLayer('zones')}
          />
          <DarkCheckbox
            checked={layers.boxes}
            colorScheme={colorScheme}
            label={t('tailgate:grid_test_v2.layer_boxes')}
            onChange={() => toggleLayer('boxes')}
          />
          <DarkCheckbox
            checked={layers.trackIds}
            colorScheme={colorScheme}
            label={t('tailgate:grid_test_v2.layer_track_ids')}
            onChange={() => toggleLayer('trackIds')}
          />
          <DarkCheckbox
            checked={layers.counts}
            colorScheme={colorScheme}
            label={t('tailgate:grid_test_v2.layer_counts')}
            onChange={() => toggleLayer('counts')}
          />
        </DarkFlex>
        <DarkBox borderRadius='12px' overflow='hidden'>
          {videoPlayer}
        </DarkBox>
        {isSidecarLoading && (
          <DarkText color={secondaryText} fontSize='13px' mt='8px'>
            {t('tailgate:grid_test_v2.overlay_loading')}
          </DarkText>
        )}
        {isSidecarError && (
          <DarkText color={secondaryText} fontSize='13px' mt='8px'>
            {t('tailgate:grid_test_v2.overlay_error')}
          </DarkText>
        )}
        {isFrameCallbackUnsupported && (
          <DarkText color={secondaryText} fontSize='13px' mt='8px'>
            {t('tailgate:grid_test_v2.frame_callback_unsupported')}
          </DarkText>
        )}
        {isCodecUnsupported && (
          <DarkText color={secondaryText} fontSize='13px' mt='8px'>
            {t('tailgate:grid_test_v2.codec_support_help')}
          </DarkText>
        )}
      </DarkBox>
    )
  }

  const secondaryText = `text.secondary.${mode}`

  return (
    <Box>
      <Flex
        border='1px solid'
        borderColor={`border.default.${mode}`}
        borderRadius='md'
        gap={4}
        mb={3}
        p={3}
        wrap='wrap'
      >
        <Checkbox
          isChecked={layers.zones && !isZonesDisabled}
          isDisabled={isZonesDisabled}
          onChange={() => toggleLayer('zones')}
        >
          {t('tailgate:grid_test_v2.layer_zones')}
        </Checkbox>
        <Checkbox
          isChecked={layers.boxes}
          onChange={() => toggleLayer('boxes')}
        >
          {t('tailgate:grid_test_v2.layer_boxes')}
        </Checkbox>
        <Checkbox
          isChecked={layers.trackIds}
          onChange={() => toggleLayer('trackIds')}
        >
          {t('tailgate:grid_test_v2.layer_track_ids')}
        </Checkbox>
        <Checkbox
          isChecked={layers.counts}
          onChange={() => toggleLayer('counts')}
        >
          {t('tailgate:grid_test_v2.layer_counts')}
        </Checkbox>
      </Flex>
      <Box borderRadius='xl' overflow='hidden'>
        {videoPlayer}
      </Box>
      {isSidecarLoading && (
        <Text color={secondaryText} fontSize='sm' mt={2}>
          {t('tailgate:grid_test_v2.overlay_loading')}
        </Text>
      )}
      {isSidecarError && (
        <Text color={secondaryText} fontSize='sm' mt={2}>
          {t('tailgate:grid_test_v2.overlay_error')}
        </Text>
      )}
      {isFrameCallbackUnsupported && (
        <Text color={secondaryText} fontSize='sm' mt={2}>
          {t('tailgate:grid_test_v2.frame_callback_unsupported')}
        </Text>
      )}
      {isCodecUnsupported && (
        <Text color={secondaryText} fontSize='sm' mt={2}>
          {t('tailgate:grid_test_v2.codec_support_help')}
        </Text>
      )}
    </Box>
  )
}
